// Notes Service - Personal place notes for user logbook

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from '@firebase/firestore';
import { db } from './firebase';
import type { PlaceNote, PlaceNoteCreateInput } from '../types';

const COLLECTION_NAME = 'userNotes';

/**
 * Remove undefined values from an object (Firestore doesn't accept undefined)
 */
function removeUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== undefined)
  ) as Partial<T>;
}

/**
 * Add a new place note
 */
export async function addPlaceNote(note: PlaceNoteCreateInput): Promise<string> {
  // Remove undefined values - Firestore doesn't accept undefined
  const cleanNote = removeUndefined(note);

  const docRef = await addDoc(collection(db, COLLECTION_NAME), {
    ...cleanNote,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return docRef.id;
}

/**
 * Get a note by ID
 */
export async function getNoteById(noteId: string): Promise<PlaceNote | null> {
  const docRef = doc(db, COLLECTION_NAME, noteId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as PlaceNote;
}

/**
 * Get all notes for a user (for logbook)
 */
export async function getUserNotes(userId: string): Promise<PlaceNote[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  })) as PlaceNote[];
}

/**
 * Get notes for a specific place by a user
 */
export async function getUserNotesForPlace(
  userId: string,
  placeId: string
): Promise<PlaceNote[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('userId', '==', userId),
    where('placeId', '==', placeId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  })) as PlaceNote[];
}

/**
 * Check if user has any notes for a place
 */
export async function hasUserNoteForPlace(
  userId: string,
  placeId: string
): Promise<boolean> {
  const notes = await getUserNotesForPlace(userId, placeId);
  return notes.length > 0;
}

/**
 * Update a note
 */
export async function updatePlaceNote(
  noteId: string,
  updates: Partial<Pick<PlaceNote,
    'text' | 'personalRating' | 'visitDate' | 'mood' | 'moods' |
    'photos' | 'photoMetadata' | 'isPublic' | 'sharedAt'
  >>
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, noteId);

  // Prepare update data, handling isPublic toggle
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateData: Record<string, any> = {
    ...updates,
    updatedAt: serverTimestamp(),
  };

  // Set sharedAt when making public for the first time
  if (updates.isPublic === true && !updates.sharedAt) {
    updateData.sharedAt = serverTimestamp();
  }

  // Remove undefined values (Firestore doesn't accept undefined)
  const cleanData = Object.fromEntries(
    Object.entries(updateData).filter(([, v]) => v !== undefined)
  );

  await updateDoc(docRef, cleanData);
}

/**
 * Delete a note
 */
export async function deletePlaceNote(noteId: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, noteId);
  await deleteDoc(docRef);
}

/**
 * Get note count for a user
 */
export async function getUserNoteCount(userId: string): Promise<number> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('userId', '==', userId)
  );

  const snapshot = await getDocs(q);
  return snapshot.size;
}

/**
 * Get unique places count for user notes
 */
export async function getUniquePlacesCount(userId: string): Promise<number> {
  const notes = await getUserNotes(userId);
  const uniquePlaces = new Set(notes.map((n) => n.placeId));
  return uniquePlaces.size;
}

/**
 * Format visit date for display
 */
export function formatVisitDate(date: Timestamp | Date | undefined): string {
  if (!date) return '';

  const d = date instanceof Timestamp ? date.toDate() : new Date(date);
  return d.toLocaleDateString('de-DE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Get notes for a specific date
 */
export async function getUserNotesForDate(
  userId: string,
  date: Date
): Promise<PlaceNote[]> {
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);

  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);

  const q = query(
    collection(db, COLLECTION_NAME),
    where('userId', '==', userId),
    where('createdAt', '>=', Timestamp.fromDate(startOfDay)),
    where('createdAt', '<=', Timestamp.fromDate(endOfDay)),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  })) as PlaceNote[];
}

/**
 * Get notes within a date range
 */
export async function getUserNotesByDateRange(
  userId: string,
  startDate: Date,
  endDate: Date
): Promise<PlaceNote[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('userId', '==', userId),
    where('createdAt', '>=', Timestamp.fromDate(startDate)),
    where('createdAt', '<=', Timestamp.fromDate(endDate)),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  })) as PlaceNote[];
}

/**
 * Get "On this day" memories - notes from the same day/month in previous years
 */
export function getMemoriesForToday(notes: PlaceNote[]): PlaceNote[] {
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentDay = today.getDate();
  const currentYear = today.getFullYear();

  return notes.filter((note) => {
    const noteDate = note.visitDate instanceof Timestamp
      ? note.visitDate.toDate()
      : note.createdAt instanceof Timestamp
        ? note.createdAt.toDate()
        : new Date(note.visitDate || note.createdAt);

    return (
      noteDate.getMonth() === currentMonth &&
      noteDate.getDate() === currentDay &&
      noteDate.getFullYear() < currentYear
    );
  });
}

/**
 * Get public notes for a user (for public profile)
 */
export async function getPublicNotesForUser(userId: string): Promise<PlaceNote[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('userId', '==', userId),
    where('isPublic', '==', true),
    orderBy('sharedAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  })) as PlaceNote[];
}

/**
 * Toggle note publicity
 */
export async function toggleNotePublicity(
  noteId: string,
  isPublic: boolean
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, noteId);

  if (isPublic) {
    await updateDoc(docRef, {
      isPublic: true,
      sharedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } else {
    await updateDoc(docRef, {
      isPublic: false,
      updatedAt: serverTimestamp(),
    });
  }
}
